/* eslint-disable @typescript-eslint/no-explicit-any */
// HeaderBar uses dynamic message/trace payloads that cannot be statically typed.
import React, { useEffect, useRef } from 'react';

import { DashedTag } from '@/components/chariot-ui';
import { Button } from '@/components/ui/button';
import {
  isUsageUnknown,
  useConversationCost,
} from '@/hooks/useConversationCost';
import { useTraceDrawerStore } from '@/sections/insights/aiAssistant/stores/traceDrawer.store';
import type { AIAssistantMode } from '@/sections/insights/aiAssistant/types';

import { CostChip } from './CostChip';
import { ToolCallsPopover } from './ToolCallsPopover';

// --- HeaderBar ---

interface HeaderBarProps {
  conversationId: string;
  messages: any[];
  mode: AIAssistantMode;
  isWaitingForResponse: boolean;
  isPlannerRunning: boolean;
  isThinking: boolean;
  selectedConversation: any;
  traces: any;
  childConversations: any[];
  hasActiveJobs?: boolean;
  loadingStatus: string;
  agentExecutions: Map<string, any>;
  expandedAgentCards: Set<string>;
  thinkingStartTime: number | null;
  showToolCallsPopover: boolean;
  setShowToolCallsPopover: (updater: (prev: boolean) => boolean) => void;
  onTracesCommand: () => void;
  onSetPendingMission: (mission: string) => void;
  /** Total pending credential requests across the conversation tree. */
  pendingCredentialCount?: number;
  /** Conversation ids with a pending credential request ("needs input"). */
  needsInputConversationIds?: Set<string>;
  /** Opens the credential-requests drawer (roster). */
  onOpenCredentialRequests?: () => void;
}

export function HeaderBar({
  conversationId,
  messages,
  mode,
  isWaitingForResponse,
  isPlannerRunning,
  isThinking,
  selectedConversation,
  traces,
  childConversations,
  hasActiveJobs,
  thinkingStartTime,
  showToolCallsPopover,
  setShowToolCallsPopover,
  onTracesCommand,
  onSetPendingMission,
  pendingCredentialCount = 0,
  needsInputConversationIds,
  onOpenCredentialRequests,
}: HeaderBarProps) {
  const anyChildNeedsInput =
    !!needsInputConversationIds &&
    childConversations.some(c =>
      needsInputConversationIds.has(c.uuid || c.id || '')
    );
  const topic =
    selectedConversation?.topic ??
    messages.find(m => m.role === 'user')?.content?.trim() ??
    `Conversation ${conversationId.slice(-8)}`;

  const {
    data: cost,
    isLoading: costLoading,
    isError: costError,
    invalidate: invalidateCost,
  } = useConversationCost(conversationId);

  // F1: cost is fetched on-demand with a 30s staleTime and never refetches on
  // its own. When a turn finishes (isPlannerRunning true→false edge), the
  // spend has changed, so invalidate the cost query once to pull a fresh total.
  // isPlannerRunning (not isWaitingForResponse) is the right signal: during a
  // normal WS-driven turn isWaitingForResponse never goes true — the running
  // state surfaces via loadingStatus / conversation status, both folded into
  // isPlannerRunning upstream. Edge-triggered (ref tracks the previous value)
  // so it fires exactly once per completion, not on every render. This is the
  // only invalidation of this key.
  const prevRunningRef = useRef(isPlannerRunning);
  useEffect(() => {
    if (prevRunningRef.current && !isPlannerRunning) {
      invalidateCost();
    }
    prevRunningRef.current = isPlannerRunning;
  }, [isPlannerRunning, invalidateCost]);

  const toolCalls = messages.filter(msg => msg.role === 'tool call');
  const hasTraces = !!(traces && traces.length > 0);

  const lastMessage = messages[messages.length - 1];
  const isActiveStatus =
    isWaitingForResponse ||
    (messages.length > 0 && lastMessage?.role !== 'chariot');

  const statusLabel = (() => {
    if (!isThinking && !isWaitingForResponse) return 'READY';
    const recent = messages.slice(-10);
    const lastCall = recent.filter(m => m.role === 'tool call').pop();
    const lastResp = recent.filter(m => m.role === 'tool response').pop();
    const isExecuting =
      lastCall &&
      (!lastResp ||
        new Date(lastCall.timestamp || '').getTime() >
          new Date(lastResp.timestamp || '').getTime());
    if (isExecuting) return 'EXECUTING';
    if (lastResp) return 'ANALYZING';
    return 'THINKING';
  })();

  const statusTitle = (() => {
    if (!isThinking || !thinkingStartTime) return 'Ready to process queries';
    // eslint-disable-next-line react-hooks/purity
    const elapsed = Math.floor((Date.now() - thinkingStartTime) / 1000);
    const min = Math.floor(elapsed / 60),
      sec = elapsed % 60;
    const timeStr = min > 0 ? `${min}m ${sec}s` : `${sec}s`;
    const recent = messages.slice(-10);
    const lastCall = recent.filter(m => m.role === 'tool call').pop();
    const lastResp = recent.filter(m => m.role === 'tool response').pop();
    const isExecuting =
      lastCall &&
      (!lastResp ||
        new Date(lastCall.timestamp || '').getTime() >
          new Date(lastResp.timestamp || '').getTime());
    if (isExecuting && lastCall) {
      try {
        const d = JSON.parse(
          (lastCall as any).toolUseContent || lastCall.content
        );
        const name = d?.Name || d?.tool || 'tool';
        const inp = d?.Input || d?.input;
        if (name === 'query' && inp?.node?.labels)
          return `Querying ${inp.node.labels.join(', ')} (${timeStr})`;
        if (name === 'job')
          return `Running ${inp?.capability || 'scan'} (${timeStr})`;
        return `Executing ${name} (${timeStr})`;
      } catch {
        return `Executing tool (${timeStr})`;
      }
    }
    if (lastResp) {
      try {
        const d = JSON.parse(lastResp.content);
        if (d.count !== undefined && d.count > 0)
          return `Analyzing ${d.count} results (${timeStr})`;
      } catch {
        /* ignore */
      }
    }
    return `Processing (${timeStr})`;
  })();

  const workflowStatus = (() => {
    if (!traces || !Array.isArray(traces) || traces.length === 0)
      return 'waiting' as const;
    const spanStatus = new Map<
      string,
      { hasStart: boolean; hasEnd: boolean }
    >();
    traces.forEach((event: any) => {
      if (!event) return;
      const tracked = [
        'agent_start',
        'agent_end',
        'job_start',
        'job_end',
        'job_queued',
      ].includes(event.event_type);
      if (!tracked) return;
      const cur = spanStatus.get(event.span_id) || {
        hasStart: false,
        hasEnd: false,
      };
      if (['agent_start', 'job_start', 'job_queued'].includes(event.event_type))
        cur.hasStart = true;
      if (['agent_end', 'job_end'].includes(event.event_type))
        cur.hasEnd = true;
      spanStatus.set(event.span_id, cur);
    });
    if (spanStatus.size === 0) return 'waiting' as const;
    const allDone = Array.from(spanStatus.values()).every(
      s => !s.hasStart || s.hasEnd
    );
    return allDone ? ('complete' as const) : ('running' as const);
  })();

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary border-b">
      <div className="flex h-14 items-center">
        {/* Topic */}
        <div className="border-chariot-stroke flex h-full min-w-0 flex-1 items-center border-r px-6">
          <span className="text-chariot-text-primary text-md truncate font-semibold">
            {topic}
          </span>
        </div>

        {/* Tool calls */}
        {toolCalls.length > 0 && (
          <div
            className={`text-chariot-text-secondary relative flex h-full items-center px-6 text-xs ${hasTraces ? 'border-chariot-stroke border-l' : ''}`}
          >
            <button
              onClick={() => setShowToolCallsPopover(prev => !prev)}
              className="hover:text-chariot-text-primary tool-calls-button underline decoration-dotted underline-offset-2"
            >
              Tools:{' '}
              <span className="inline-block min-w-[1.25rem] text-right tabular-nums">
                {toolCalls.length}
              </span>
            </button>
            {showToolCallsPopover && (
              <ToolCallsPopover
                toolCalls={toolCalls}
                messages={messages}
                onClose={() => setShowToolCallsPopover(() => false)}
              />
            )}
          </div>
        )}

        {/* Status */}
        <div className="border-chariot-stroke flex h-full items-center gap-3 border-l px-6">
          <span className="text-chariot-text-primary text-sm font-medium">
            Status:
          </span>
          <div title={statusTitle}>
            <DashedTag
              label={statusLabel}
              active={false}
              size="xs"
              className="min-w-[5.5rem] font-normal"
              icon={
                <div
                  className={`size-2 rounded-full ${isActiveStatus ? 'bg-success animate-pulse' : 'bg-chariot-text-secondary'}`}
                />
              }
            />
          </div>
        </div>

        {/* Cost — hide the bordered cell entirely when the chip would render
            nothing (settled null/404 OR a 200 whose cost is null), so we never
            show an empty bordered box. CostChip itself returns null on
            cost == null; this gate keeps the surrounding cell from rendering. */}
        {(costLoading || costError || cost != null) && (
          <div className="border-chariot-stroke flex h-full items-center gap-3 border-l px-6">
            <CostChip
              label="Cost"
              cost={cost?.total?.cost}
              currency={cost?.currency}
              unknown={isUsageUnknown(cost)}
              isLoading={costLoading}
              isError={costError}
            />
          </div>
        )}

        {/* Workflow */}
        {selectedConversation?.trace_id && (
          <div className="border-chariot-stroke flex h-full items-center gap-3 border-l px-6">
            <span className="text-chariot-text-primary text-sm font-medium">
              Workflow:
            </span>
            <button
              onClick={onTracesCommand}
              className="hover:text-chariot-text-primary flex cursor-pointer items-center gap-1.5"
              title="Click to view workflow details"
            >
              {workflowStatus === 'waiting' && (
                <>
                  <div className="border-chariot-stroke size-2 rounded-full border-2" />
                  <span className="text-chariot-text-secondary inline-block min-w-[4.5rem] text-sm">
                    Waiting
                  </span>
                </>
              )}
              {workflowStatus === 'complete' && (
                <>
                  <span className="text-success">✓</span>
                  <span className="text-chariot-text-secondary inline-block min-w-[4.5rem] text-sm">
                    Complete
                  </span>
                </>
              )}
              {workflowStatus === 'running' && (
                <>
                  <div className="size-2 animate-pulse rounded-full bg-blue-500" />
                  <span className="text-chariot-text-secondary inline-block min-w-[4.5rem] text-sm">
                    Running
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Jobs (only when no trace_id — otherwise the Workflow opener covers it) */}
        {hasActiveJobs && !selectedConversation?.trace_id && (
          <div className="border-chariot-stroke flex h-full items-center gap-2 border-l px-6">
            <button
              onClick={() => {
                useTraceDrawerStore.getState().openTab('jobs');
              }}
              className="text-chariot-text-secondary hover:text-chariot-text-primary flex items-center gap-1.5 text-sm"
              title="View background jobs"
              aria-label="View background jobs"
            >
              Jobs
              <span className="relative flex size-2">
                <span className="bg-chariot-brand-primary absolute inline-flex size-full animate-ping rounded-full opacity-75" />
                <span className="bg-chariot-brand-primary relative inline-flex size-2 rounded-full" />
              </span>
            </button>
          </div>
        )}

        {/* Needs input — durable opener for the credential-requests drawer.
            Amber (status-warning), distinct from the green "live" ping, with a
            text label so it is never color-only. (ENG-3976 B+C hybrid) */}
        {pendingCredentialCount > 0 && (
          <div className="border-chariot-stroke flex h-full items-center gap-2 border-l px-6">
            <button
              onClick={() => onOpenCredentialRequests?.()}
              className="text-chariot-status-warning hover:text-chariot-status-warning/80 flex items-center gap-1.5 text-sm font-medium"
              title="Subagent needs credentials — open requests"
              aria-label={`${pendingCredentialCount} subagent credential request${
                pendingCredentialCount !== 1 ? 's' : ''
              } need input — open requests`}
            >
              <span className="relative flex size-2">
                <span className="bg-chariot-status-warning absolute inline-flex size-full rounded-full opacity-75 motion-safe:animate-ping" />
                <span className="bg-chariot-status-warning relative inline-flex size-2 rounded-full" />
              </span>
              Needs input
              <span className="bg-chariot-status-warning/15 inline-block min-w-[0.75rem] rounded-full px-1.5 text-center text-[10px] font-semibold tabular-nums">
                {pendingCredentialCount}
              </span>
            </button>
          </div>
        )}

        {/* Subagents */}
        {childConversations.length > 0 && (
          <div className="border-chariot-stroke flex h-full items-center gap-2 border-l px-6">
            <button
              onClick={() => {
                useTraceDrawerStore.getState().openTab('subagents');
              }}
              className="text-chariot-text-secondary hover:text-chariot-text-primary flex items-center gap-1.5 text-sm"
              title="View subagent conversations"
            >
              Subagents
              <span className="bg-chariot-background-secondary inline-block min-w-[0.75rem] rounded-full px-1.5 text-center text-[10px] font-medium tabular-nums">
                {childConversations.length}
              </span>
              {childConversations.some(c => c.status === 'active') && (
                <span className="relative flex size-2">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                </span>
              )}
              {anyChildNeedsInput && (
                <span
                  role="img"
                  aria-label="a subagent needs input"
                  className="relative flex size-2"
                >
                  <span className="bg-chariot-status-warning absolute inline-flex size-full rounded-full opacity-75 motion-safe:animate-ping" />
                  <span className="bg-chariot-status-warning relative inline-flex size-2 rounded-full" />
                </span>
              )}
            </button>
          </div>
        )}

        {/* Launch Hannibal */}
        {mode === 'auto' && (
          <div className="border-chariot-stroke flex h-full items-center border-l px-4">
            <Button
              size="sm"
              variant="outline"
              data-tutorial="ai-launch-hannibal"
              onClick={() => {
                const lastAssistantMsg = messages
                  ?.filter((m: any) => m.role === 'assistant')
                  ?.at(-1)?.content;
                onSetPendingMission(lastAssistantMsg || '');
              }}
            >
              Launch Hannibal
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
